import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { BillingReadiness } from "@/components/BillingReadiness";

export function Billing({ stats, onRefresh }) {
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(null);
  const [billing, setBilling] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => toast.error("Could not load subscription plans."));
    api.get("/billing/status", { params: { origin_url: window.location.origin } }).then(({ data }) => setBilling(data)).catch(() => setBilling({ unavailable: true, checkout_enabled: false }));
  }, []);

  const current = stats?.plan || "free";

  const checkout = async (lookupKey) => {
    if (!lookupKey) return;
    setBusy(lookupKey);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not start checkout.");
      setBusy(null);
    }
  };

  const manage = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open subscription management.");
      setBusy(null);
    }
  };

  const featureFor = (p) => [
    `${p.clips.toLocaleString()} clips / month`,
    "Watermark-free exports",
    "Speaker framing & captions",
    "Custom templates",
  ];

  return (
    <div data-testid="billing-panel">
      <div className="mb-6">
        <h2 className="font-display text-2xl font-bold">Plans & billing</h2>
        <p className="text-sm text-copy-500 mt-1">
          You're on the <span className="text-cyan-400 font-semibold">{stats?.plan_name || "Free"}</span> plan
          {stats ? ` · ${stats.used_this_month}/${stats.limit} clips used this month` : ""}.
        </p>
      </div>

      <div data-testid="billing-mode-banner" className={`mb-6 rounded-xl border px-4 py-3 text-sm ${billing?.mode === 'live' && billing.checkout_enabled ? 'border-emerald-500/30 text-emerald-300' : 'border-amber-500/20 text-amber-200'}`}>
        {!billing ? "Checking payment configuration…" : billing.return_origin_verified === false ? "Payments are disabled: this site's return address needs to be configured by the owner." : billing.unavailable ? "Payment verification is temporarily unavailable. Checkout is disabled." : billing.mode === "test" ? "TEST MODE — these are test subscriptions, not real charges." : billing.checkout_enabled ? "LIVE PAYMENTS — subscriptions will charge real money." : "Live payments are not ready. Checkout is disabled until activation checks pass."}
      </div>
      {stats?.has_subscription && <div data-testid="subscription-management" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-surface-800 p-4">
        <p data-testid="subscription-status" className="text-sm text-copy-400">{stats.cancel_at_period_end ? "Cancellation scheduled for the end of the current billing period." : `Subscription status: ${stats.subscription_status || "existing"}`}</p>
        <Button data-testid="manage-subscription-button" onClick={manage} disabled={!!busy} variant="outline" className="border-surface-700 bg-transparent">Manage subscription</Button>
      </div>}
      <div className="grid md:grid-cols-3 gap-5">
        {plans.map((p) => {
          const isCurrent = p.id === current;
          const highlight = p.id === "pro";
          return (
            <div key={p.id} data-testid={`plan-${p.id}`}
              className={`rounded-3xl border p-7 relative ${highlight ? "border-cyan-500/40 bg-gradient-to-b from-cyan-500/[0.06] to-transparent" : "border-surface-800 bg-panel"}`}>
              {isCurrent && (
                <span className="absolute top-6 right-6 text-[10px] uppercase tracking-widest text-black bg-cyan-400 rounded-full px-2 py-0.5 font-bold">Current</span>
              )}
              <div className="flex items-center gap-2">
                <Zap className={`h-5 w-5 ${highlight ? "text-cyan-400" : "text-copy-500"}`} fill="currentColor" />
                <h3 className="font-display text-xl font-bold">{p.name}</h3>
              </div>
              <p className="font-display text-4xl font-black mt-4">
                ${p.price}<span className="text-base font-medium text-copy-500">/mo</span>
              </p>
              <ul className="space-y-3 mt-6 text-sm text-copy-300">
                {featureFor(p).map((f) => (
                  <li key={f} className="flex items-center gap-2"><Check className="h-4 w-4 text-cyan-400" /> {f}</li>
                ))}
              </ul>
              <Button
                data-testid={`plan-cta-${p.id}`}
                disabled={isCurrent || p.price === 0 || !!busy || !billing?.checkout_enabled}
                onClick={() => stats?.has_subscription ? manage() : checkout(p.lookup_key)}
                className={`w-full mt-7 rounded-full font-semibold ${highlight ? "bg-cyan-500 text-black hover:bg-cyan-400" : "bg-surface-100 text-black hover:bg-white"} disabled:opacity-40`}>
                {busy === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> :
                  isCurrent ? "Your plan" : p.price === 0 ? "Free forever" : stats?.has_subscription ? "Manage current plan" : `${billing?.mode === "test" ? "Test" : "Subscribe to"} ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>
      {billing?.mode === "test" && <p data-testid="test-card-guidance" className="text-xs text-copy-500 mt-6">Test checkout only: use 4242 4242 4242 4242, a future date and any test CVC. Do not enter real card details.</p>}
      <p data-testid="billing-price-note" className="mt-3 text-xs text-copy-500">Prices are in USD per month. Any applicable tax is shown in Stripe before confirmation.</p>
      {user?.role === "admin" && <div className="mt-8"><BillingReadiness onStatusChange={setBilling} /></div>}
    </div>
  );
}
